import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api, { FilterPreferences } from '../../services/api';
import { useListLayoutPreferences } from '../../hooks/useListLayoutPreferences';
import { useFilterPreferences } from '../../hooks/useFilterPreferences';
import { useTicketEvents } from '../../hooks/useTicketEvents';
import { getDurationPresetRange, DurationPreset } from '../dashboard/utils/dateHelpers';
import ListLayoutSettingsModal from '../../components/ListLayoutSettingsModal';
import ListLayoutSettingsButton from '../../components/ListLayoutSettingsButton';
import RefreshButton from '../../components/RefreshButton';
import FilterDropdown, { FilterSection } from '../../components/FilterDropdown';
import ActiveFilterChips, { FilterChip } from '../../components/ActiveFilterChips';
import TableColumnFilter from '../../components/TableColumnFilter';
import { useAuth } from '../../contexts/AuthContext';
import { useIsServiceDeskUser } from '../../hooks/useIsServiceDeskUser';
import { trackServiceRequestOperation } from '../../utils/appInsights';
import { usePersistListFiltersSession } from '../../hooks/usePersistListFiltersSession';
import { LIST_FILTER_SESSION_KEYS } from '../../constants/listFilterSessionKeys';
import { isTicketSearchInputValid } from '../../utils/searchTerms';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
  ClockIcon,
  CheckIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  EnvelopeIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import EmailTicketModal from '../../components/EmailTicketModal';
import GenericExportButton from '../../components/GenericExportButton';
import { ExportConfigs } from '../../services/genericExportService';

interface ServiceRequest {
  id?: string;
  _id?: string;
  number: string;
  shortDescription: string;
  description?: string;
  priority: string;
  state: string;
  category: string;
  channel: string;
  impact: string;
  urgency: string;
  approvalRequired?: boolean;
  canEdit?: boolean;
  contact?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  assignedTo?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  Customer?: {
    id: string;
    accountName: string;
    accountShortName?: string;
  };
  Engagement?: {
    id: string;
    name: string;
    status: string;
  };
  ServicePlan?: {
    id: string;
    name: string;
  };
  Resource?: {
    id: string;
    name: string;
  };
  AssignmentGroup?: {
    id: string;
    name: string;
  };
  resourceIdValue?: string;
  createdAt: string;
  updatedAt: string;
  createdById?: string;
  createdBy?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  slaStatus?: Array<{
    targetType: string;
    currentState: string;
    isBreached: boolean;
  }>;
}

// SLA Status display component with icons and tooltips
interface SlaStatusItem {
  targetType: string;
  currentState: string;
  isBreached: boolean;
}

const SlaStatusDisplay: React.FC<{ slaStatus?: SlaStatusItem[] }> = ({ slaStatus }) => {
  if (!slaStatus || slaStatus.length === 0) {
    return <span className="text-gray-400">-</span>;
  }

  const getStatusIcon = (item: SlaStatusItem) => {
    if (item.isBreached || item.currentState === 'BREACHED') return '🔴';
    if (item.currentState === 'ACHIEVED' || item.currentState === 'COMPLETED') return '✅';
    if (item.currentState === 'RUNNING') return '⏳';
    if (item.currentState === 'PAUSED') return '⏸️';
    if (item.currentState === 'CANCELLED') return '⚪';
    return '⏳';
  };

  const getTargetLabel = (targetType: string) => {
    switch (targetType) {
      case 'RESPONSE': return 'R';
      case 'ONGOING': return 'O';
      case 'RESOLUTION': return 'S';
      default: return targetType.charAt(0);
    }
  };

  const getFullTargetName = (targetType: string) => {
    switch (targetType) {
      case 'RESPONSE': return 'Response SLA';
      case 'ONGOING': return 'Ongoing SLA';
      case 'RESOLUTION': return 'Resolution SLA';
      default: return targetType;
    }
  };

  const getStatusText = (item: SlaStatusItem) => {
    if (item.isBreached || item.currentState === 'BREACHED') return 'Breached';
    if (item.currentState === 'ACHIEVED' || item.currentState === 'COMPLETED') return 'Met';
    if (item.currentState === 'RUNNING') return 'Running';
    if (item.currentState === 'PAUSED') return 'Paused';
    if (item.currentState === 'CANCELLED') return 'Cancelled';
    return item.currentState;
  };

  const sortOrder = { 'RESPONSE': 1, 'ONGOING': 2, 'RESOLUTION': 3 };
  const sortedStatus = [...slaStatus].sort((a, b) =>
    (sortOrder[a.targetType as keyof typeof sortOrder] || 99) -
    (sortOrder[b.targetType as keyof typeof sortOrder] || 99)
  );

  return (
    <div className="flex items-center gap-1">
      {sortedStatus.map((item, index) => (
        <div key={item.targetType} className="relative group inline-flex items-center">
          <span className="cursor-help text-xs font-medium text-gray-600">{getTargetLabel(item.targetType)}</span>
          <span className="text-sm ml-0.5">{getStatusIcon(item)}</span>
          {index < sortedStatus.length - 1 && <span className="text-gray-300 mx-0.5">|</span>}
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
            {getFullTargetName(item.targetType)}: {getStatusText(item)}
            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
          </div>
        </div>
      ))}
    </div>
  );
};

const priorityColors = {
  'critical_1': 'bg-red-100 text-red-800 border-red-200',
  'high_2': 'bg-orange-100 text-orange-800 border-orange-200',
  'moderate_3': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'low_4': 'bg-blue-100 text-blue-800 border-blue-200',
  'planning_5': 'bg-gray-100 text-gray-800 border-gray-200'
};

const priorityLabels = {
  'critical_1': 'Critical',
  'high_2': 'High',
  'moderate_3': 'Moderate',
  'low_4': 'Low',
  'planning_5': 'Planning'
};


const stateIcons = {
  'new': <DocumentTextIcon className="h-5 w-5 text-gray-500" />,
  'in_progress': <ExclamationTriangleIcon className="h-5 w-5 text-orange-500" />,
  'pending_approval': <ClockIcon className="h-5 w-5 text-purple-500" />,
  'on_hold': <InformationCircleIcon className="h-5 w-5 text-yellow-500" />,
  'approved': <CheckCircleIcon className="h-5 w-5 text-green-500" />,
  'closed': <CheckIcon className="h-5 w-5 text-gray-500" />,
  'cancelled': <XMarkIcon className="h-5 w-5 text-red-500" />
};


const stateLabels = {
  'new': 'New',
  'in_progress': 'In Progress',
  'pending_approval': 'Pending Approval',
  'on_hold': 'On Hold',
  'approved': 'Approved',
  'closed': 'Closed',
  'cancelled': 'Cancelled'
};

// Helper function to strip HTML tags and decode HTML entities
const stripHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

// Truncated text with tooltip component
const TruncatedTextWithTooltip: React.FC<{ text: string | null | undefined; maxLength?: number }> = ({ text, maxLength = 50 }) => {
  if (!text) {
    return <span className="text-gray-400">-</span>;
  }

  const strippedText = stripHtml(text);
  const firstLine = strippedText.split('\n')[0];
  const displayText = firstLine.length > maxLength ? firstLine.substring(0, maxLength) + '...' : firstLine;
  const needsTooltip = strippedText.length > maxLength || strippedText.includes('\n');

  if (!needsTooltip) {
    return <span>{displayText}</span>;
  }

  return (
    <div className="relative group">
      <span className="cursor-help">{displayText}</span>
      <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded max-w-md whitespace-pre-wrap break-all opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 shadow-lg">
        {strippedText}
        <div className="absolute top-full left-4 border-4 border-transparent border-t-gray-800"></div>
      </div>
    </div>
  );
};

// Options for multi-select filters
const stateOptions = [
  { value: 'new', label: 'New' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const priorityOptions = [
  { value: 'critical_1', label: 'Critical' },
  { value: 'high_2', label: 'High' },
  { value: 'moderate_3', label: 'Moderate' },
  { value: 'low_4', label: 'Low' },
  { value: 'planning_5', label: 'Planning' },
];

const timeRangeOptions = [
  { value: '5', label: 'Last 5 mins' },
  { value: '15', label: 'Last 15 mins' },
  { value: '30', label: 'Last 30 mins' },
  { value: '60', label: 'Last 1 hour' },
  { value: '120', label: 'Last 2 hours' },
  { value: '480', label: 'Last 8 hours' },
  { value: '1440', label: 'Last 24 hours' },
  { value: 'preset:this_week', label: 'This Week' },
  { value: 'preset:last_week', label: 'Last Week' },
  { value: 'preset:this_month', label: 'This Month' },
  { value: 'preset:last_month', label: 'Last Month' },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

const impactOptions = [
  { value: 'high_1', label: '1 - High' },
  { value: 'medium_2', label: '2 - Medium' },
  { value: 'low_3', label: '3 - Low' },
];

const urgencyOptions = [
  { value: 'high_1', label: '1 - High' },
  { value: 'medium_2', label: '2 - Medium' },
  { value: 'low_3', label: '3 - Low' },
];

const channelOptions = [
  { value: 'web', label: 'Web' },
  { value: 'phone', label: 'Phone' },
  { value: 'email', label: 'Email' },
  { value: 'alert', label: 'Alert' },
];

const SERVICE_REQUEST_LIST_URL_FILTER_KEYS = [
  'search',
  'number',
  'desc',
  'contact',
  'state',
  'priority',
  'time',
  'customer',
  'engagement',
  'alert',
  'groups',
  'assignedToMe',
  'assignedTo',
  'unassigned',
  'category',
  'channel',
  'impact',
  'urgency',
  'createdBy',
  'sla',
] as const;

function isDefaultServiceRequestListUrl(sp: URLSearchParams): boolean {
  const page = sp.get('page');
  if (page && page !== '1') return false;
  const ps = sp.get('pageSize');
  if (ps && ps !== '25') return false;
  for (const k of SERVICE_REQUEST_LIST_URL_FILTER_KEYS) {
    const v = sp.get(k);
    if (v !== null && v !== '') return false;
  }
  return true;
}

function buildServiceRequestListUrlUpdatesFromSaved(
  savedFilters: FilterPreferences
): Record<string, string | null | undefined> {
  const updates: Record<string, string | null | undefined> = {};
  if (savedFilters.state?.length) updates.state = savedFilters.state.join(',');
  if (savedFilters.priority?.length) updates.priority = savedFilters.priority.join(',');
  if (savedFilters.timeRange === '') {
    updates.time = 'all';
  } else if (savedFilters.timeRange && savedFilters.timeRange !== 'preset:this_month') {
    updates.time = savedFilters.timeRange;
  }
  if (savedFilters.customer) updates.customer = savedFilters.customer;
  if (savedFilters.engagement) {
    updates.engagement = savedFilters.engagement;
  }
  if (savedFilters.assignmentGroup) {
    updates.groups = savedFilters.assignmentGroup;
  }
  if (savedFilters.assignedToMe !== undefined && savedFilters.assignedToMe !== null) {
    updates.assignedToMe =
      savedFilters.assignedToMe === true || savedFilters.assignedToMe === 'true' ? 'true' : 'false';
  }
  if (savedFilters.assignedTo) updates.assignedTo = savedFilters.assignedTo;
  if (savedFilters.unassigned) updates.unassigned = savedFilters.unassigned;
  return updates;
}

function serializeServiceRequestTimeRangeForUrl(timeRange: string): string | null {
  if (!timeRange) return 'all';
  if (timeRange === 'preset:this_month') return null;
  return timeRange;
}

const ServiceRequestList: React.FC = () => {
  // Subscribe to real-time service request events via SSE
  useTicketEvents({ ticketTypes: ['SERVICE_REQUEST'] });

  const { user } = useAuth();
  const isServiceDeskUser = useIsServiceDeskUser();
  const hasLoggedView = useRef(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  usePersistListFiltersSession('/service-requests', LIST_FILTER_SESSION_KEYS.serviceRequests);

  const patchSearchParams = useCallback(
    (updates: Record<string, string | null | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, val] of Object.entries(updates)) {
            if (val === null || val === undefined || val === '') {
              next.delete(key);
            } else {
              next.set(key, val);
            }
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') ?? '');
  const appliedSearchTerm = searchParams.get('search') ?? '';
  useEffect(() => {
    setSearchInput(appliedSearchTerm);
  }, [appliedSearchTerm]);

  const page = useMemo(() => {
    const p = parseInt(searchParams.get('page') || '1', 10);
    return Number.isFinite(p) && p >= 1 ? p : 1;
  }, [searchParams]);

  const pageSize = useMemo(() => {
    const ps = parseInt(searchParams.get('pageSize') || '25', 10);
    return PAGE_SIZE_OPTIONS.includes(ps) ? ps : 25;
  }, [searchParams]);

  const filterState = useMemo(
    () => (searchParams.get('state') || '').split(',').filter(Boolean),
    [searchParams]
  );
  const filterCategory = searchParams.get('category') ?? '';
  const filterChannel = searchParams.get('channel') ?? '';
  const filterPriority = useMemo(
    () => (searchParams.get('priority') || '').split(',').filter(Boolean),
    [searchParams]
  );
  const filterTimeRange = useMemo(() => {
    const t = searchParams.get('time');
    if (t === 'all') return '';
    if (t === null || t === '') return 'preset:this_month';
    return t;
  }, [searchParams]);

  const filterCustomer = searchParams.get('customer') ?? '';
  const filterEngagement = useMemo(
    () => (searchParams.get('engagement') || '').split(',').filter(Boolean),
    [searchParams]
  );
  const appliedAlertNumber = searchParams.get('alert') ?? '';
  const [filterAlertNumber, setFilterAlertNumber] = useState(() => searchParams.get('alert') ?? '');
  useEffect(() => {
    setFilterAlertNumber(appliedAlertNumber);
  }, [appliedAlertNumber]);

  const filterAssignmentGroup = useMemo(
    () => (searchParams.get('groups') || '').split(',').filter(Boolean),
    [searchParams]
  );
  const filterAssignedToMe = searchParams.get('assignedToMe') ?? '';
  const filterAssignedTo = searchParams.get('assignedTo') ?? '';
  const filterUnassigned = searchParams.get('unassigned') ?? '';
  const filterNumber = searchParams.get('number') ?? '';
  const filterDescription = searchParams.get('desc') ?? '';
  const filterContact = searchParams.get('contact') ?? '';
  const filterCreatedBy = searchParams.get('createdBy') ?? '';
  const filterImpact = useMemo(
    () => (searchParams.get('impact') || '').split(',').filter(Boolean),
    [searchParams]
  );
  const filterUrgency = useMemo(
    () => (searchParams.get('urgency') || '').split(',').filter(Boolean),
    [searchParams]
  );
  const filterSlaBreached = searchParams.get('sla') === '1';

  const effectiveSearch = appliedSearchTerm || filterNumber || filterDescription;

  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const savedPrefsHydratedRef = useRef(false);

  // Get browser's timezone offset (in minutes from UTC)
  // This is used to ensure date filtering is consistent with user's local timezone
  const timezoneOffset = useMemo(() => new Date().getTimezoneOffset(), []);

  // Toast notification state
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null);

  // Filter preferences
  const {
    filters: savedFilters,
    saveFilters,
    isSaving: isFilterSaving,
    isFetched: isFilterPrefsFetched,
  } = useFilterPreferences('SERVICE_REQUEST');

  // Apply saved preferences to URL once when URL is still default (no explicit filters)
  useEffect(() => {
    if (filtersInitialized) return;
    if (user?.id && !isFilterPrefsFetched) return;

    if (!isDefaultServiceRequestListUrl(searchParams)) {
      setFiltersInitialized(true);
      return;
    }

    if (savedPrefsHydratedRef.current) {
      setFiltersInitialized(true);
      return;
    }
    savedPrefsHydratedRef.current = true;

    const updates = buildServiceRequestListUrlUpdatesFromSaved(savedFilters);
    if (Object.keys(updates).length > 0) {
      patchSearchParams(updates);
    }
    setFiltersInitialized(true);
  }, [
    filtersInitialized,
    isFilterPrefsFetched,
    user?.id,
    searchParams,
    savedFilters,
    patchSearchParams,
  ]);

  // Check if current filters differ from saved
  const savedEngagement = savedFilters.engagement
    ? (typeof savedFilters.engagement === 'string' ? savedFilters.engagement.split(',') : savedFilters.engagement)
    : [];
  const savedAssignmentGroup = savedFilters.assignmentGroup
    ? (typeof savedFilters.assignmentGroup === 'string' ? savedFilters.assignmentGroup.split(',') : savedFilters.assignmentGroup)
    : [];
  // Convert saved assignedToMe to string for comparison ('' if not set, 'true' or 'false' if set)
  const savedAssignedToMe = savedFilters.assignedToMe !== undefined && savedFilters.assignedToMe !== null
    ? (savedFilters.assignedToMe === true || savedFilters.assignedToMe === 'true' ? 'true' : 'false')
    : '';
  const hasUnsavedFilterChanges = filtersInitialized && (
    JSON.stringify(filterState.sort()) !== JSON.stringify((savedFilters.state || []).sort()) ||
    JSON.stringify(filterPriority.sort()) !== JSON.stringify((savedFilters.priority || []).sort()) ||
    filterTimeRange !== (savedFilters.timeRange || '') ||
    filterCustomer !== (savedFilters.customer || '') ||
    JSON.stringify([...filterEngagement].sort()) !== JSON.stringify([...savedEngagement].sort()) ||
    JSON.stringify([...filterAssignmentGroup].sort()) !== JSON.stringify([...savedAssignmentGroup].sort()) ||
    filterAssignedToMe !== savedAssignedToMe ||
    filterAssignedTo !== (savedFilters.assignedTo || '') ||
    filterUnassigned !== (savedFilters.unassigned || '')
  );

  const handleSaveFilters = async () => {
    await saveFilters({
      state: filterState,
      priority: filterPriority,
      timeRange: filterTimeRange || undefined,
      customer: filterCustomer || undefined,
      engagement: filterEngagement.length > 0 ? filterEngagement.join(',') : undefined,
      assignmentGroup: filterAssignmentGroup.length > 0 ? filterAssignmentGroup.join(',') : undefined,
      assignedToMe: filterAssignedToMe || undefined,
      assignedTo: filterAssignedTo || undefined,
      unassigned: filterUnassigned || undefined,
    });
  };


  // Email modal state
  const [emailModalState, setEmailModalState] = useState({
    isOpen: false,
    ticketId: '',
    ticketNumber: '',
    shortDescription: ''
  });

  const openEmailModal = (request: ServiceRequest) => {
    setEmailModalState({
      isOpen: true,
      ticketId: request.id || request._id || '',
      ticketNumber: request.number,
      shortDescription: request.shortDescription
    });
  };

  // List layout settings
  const [showLayoutSettings, setShowLayoutSettings] = useState(false);
  const {
    columns: layoutColumns,
    visibleColumns,
    saveColumns,
    resetToDefault,
    isSaving: isLayoutSaving,
    isResetting: isLayoutResetting,
  } = useListLayoutPreferences('SERVICE_REQUEST');

  // Export filters - build from current filter state
  const exportFilters = useMemo(() => {
    let timeRangeParam = '';
    let dateFrom = '';
    let dateTo = '';
    if (filterTimeRange) {
      if (filterTimeRange.startsWith('custom:')) {
        const [, dates] = filterTimeRange.split('custom:');
        const [from, to] = dates.split(',');
        dateFrom = from;
        dateTo = to;
      } else if (filterTimeRange.startsWith('preset:')) {
        const presetName = filterTimeRange.replace('preset:', '') as DurationPreset;
        const range = getDurationPresetRange(presetName);
        dateFrom = range.from;
        dateTo = range.to;
      } else {
        timeRangeParam = filterTimeRange;
      }
    }

    return {
      ...(effectiveSearch ? { search: effectiveSearch } : {}),
      ...(filterState.length > 0 ? { state: filterState.join(',') } : {}),
      ...(filterCategory ? { category: filterCategory } : {}),
      ...(filterChannel ? { channel: filterChannel } : {}),
      ...(filterPriority.length > 0 ? { priority: filterPriority.join(',') } : {}),
      ...(timeRangeParam ? { timeRange: timeRangeParam } : {}),
      ...(dateFrom ? { dateFrom } : {}),
      ...(dateTo ? { dateTo } : {}),
      ...(filterCustomer ? { customerId: filterCustomer } : {}),
      ...(filterEngagement.length > 0 ? { engagementId: filterEngagement.join(',') } : {}),
      ...(appliedAlertNumber ? { alertNumber: appliedAlertNumber } : {}),
      ...(filterAssignmentGroup.length > 0
        ? { assignmentGroupId: filterAssignmentGroup.join(',') }
        : {}),
      ...(filterAssignedToMe ? { assignedToMe: filterAssignedToMe } : {}),
      ...(filterAssignedTo ? { assignedToId: filterAssignedTo } : {}),
      ...(filterUnassigned ? { unassigned: filterUnassigned } : {}),
      ...(filterImpact.length > 0 ? { impact: filterImpact.join(',') } : {}),
      ...(filterUrgency.length > 0 ? { urgency: filterUrgency.join(',') } : {}),
      ...(filterContact ? { callerId: filterContact } : {}),
      ...(filterCreatedBy ? { createdById: filterCreatedBy } : {}),
      ...(filterSlaBreached ? { slaBreached: 'true' } : {}),
    };
  }, [effectiveSearch, filterState, filterCategory, filterChannel, filterPriority, filterTimeRange, filterCustomer, filterEngagement, appliedAlertNumber, filterAssignmentGroup, filterAssignedToMe, filterAssignedTo, filterUnassigned, filterImpact, filterUrgency, filterContact, filterCreatedBy, filterSlaBreached]);

  // Export columns - build from visible columns. Replace single "SLA Breached" with
  // separate Response/Ongoing/Resolution SLA Status columns in CSV.
  const exportColumns = useMemo(() => {
    const cols: { key: string; header: string }[] = [];
    for (const col of visibleColumns) {
      if (col.key === 'slaBreached') {
        cols.push({ key: 'responseSlaStatus', header: 'Response SLA Status' });
        cols.push({ key: 'ongoingSlaStatus', header: 'Ongoing SLA Status' });
        cols.push({ key: 'resolutionSlaStatus', header: 'Resolution SLA Status' });
      } else if (col.key === 'customer') {
        cols.push({ key: col.key, header: col.label || col.key });
        cols.push({ key: 'customerType', header: 'Customer Type' });
      } else {
        cols.push({ key: col.key, header: col.label || col.key });
      }
    }
    return cols;
  }, [visibleColumns]);

  // Fetch customers for dropdown
  const { data: customersData } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      const response = await api.get('/customers');
      return response.data;
    }
  });

  // Fetch assignment groups for filter dropdowns
  const { data: assignmentGroupsData } = useQuery({
    queryKey: ['assignment-groups'],
    queryFn: async () => {
      const response = await api.get('/groups/assignment-groups');
      return response.data || [];
    },
  });

  const mapDropdownUserToOption = (u: { id: string; name?: string; email?: string }) => ({
    value: u.id,
    label: u.name || u.email || u.id,
  });

  const { data: usersData } = useQuery({
    queryKey: ['users-for-filter'],
    queryFn: async () => {
      const response = await api.get('/users/dropdown');
      return response.data;
    },
  });

  const { data: contactFilterOptions } = useQuery({
    queryKey: ['contact-filter-options', filterCustomer],
    queryFn: async () => {
      const contactParams = new URLSearchParams();
      if (filterCustomer) contactParams.set('customerId', filterCustomer);

      const [contactsRes, usersRes] = await Promise.all([
        api.get(`/contacts/dropdown${contactParams.toString() ? `?${contactParams}` : ''}`),
        api.get('/users/dropdown'),
      ]);

      const options: Array<{ value: string; label: string }> = [];
      const seen = new Set<string>();

      for (const contact of contactsRes.data?.contacts ?? []) {
        if (!contact.id || seen.has(contact.id)) continue;
        seen.add(contact.id);
        options.push({
          value: contact.id,
          label: contact.name || contact.email || contact.id,
        });
      }

      for (const u of usersRes.data?.users ?? []) {
        if (!u.id || seen.has(u.id)) continue;
        seen.add(u.id);
        options.push(mapDropdownUserToOption(u));
      }

      options.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
      return options;
    },
  });

  // Fetch engagements for selected customer (use UI state for immediate dropdown update)
  const { data: engagementsData } = useQuery({
    queryKey: ['engagements', filterCustomer],
    queryFn: async () => {
      if (!filterCustomer) return { engagements: [] };
      const response = await api.get(`/engagements/dropdown?customerId=${filterCustomer}`);
      return response.data;
    },
    enabled: !!filterCustomer
  });

  // Build filter sections for FilterDropdown
  const filterSections: FilterSection[] = useMemo(() => [
    {
      id: 'state',
      label: 'State',
      type: 'multi-select',
      options: stateOptions,
      placeholder: 'All States',
    },
    {
      id: 'priority',
      label: 'Priority',
      type: 'multi-select',
      options: priorityOptions,
      placeholder: 'All Priorities',
    },
    {
      id: 'timeRange',
      label: 'Time Range',
      type: 'time-range',
      options: timeRangeOptions,
      placeholder: 'All Time',
    },
    {
      id: 'customer',
      label: 'Customer',
      type: 'searchable-select',
      options: customersData?.customers?.map((c: any) => ({
        value: c.id,
        label: c.accountName || c.name,
      })) || [],
      placeholder: 'All Customers',
    },
    {
      id: 'engagement',
      label: 'Engagement',
      type: 'searchable-multi-select',
      options: engagementsData?.engagements?.map((e: any) => ({
        value: e.id,
        label: e.name,
      })) || [],
      placeholder: 'All Engagements',
      disabled: !filterCustomer,
      disabledMessage: 'Select a customer first',
    },
    {
      id: 'alertNumber',
      label: 'Alert Number',
      type: 'text',
      placeholder: 'e.g., ALT001, ALT002',
      helpText: 'Enter single or multiple alert numbers separated by commas',
    },
    {
      id: 'assignmentGroup',
      label: 'Assignment Group',
      type: 'searchable-multi-select',
      options: assignmentGroupsData?.map((g: { id: string; name: string }) => ({
        value: g.id,
        label: g.name,
      })) || [],
      placeholder: 'All Groups',
    },
    {
      id: 'assignedToMe',
      label: 'Assigned To Me',
      type: 'single-select',
      options: [
        { value: 'true', label: 'Yes' },
        { value: 'false', label: 'No' },
      ],
      placeholder: 'All Assignments',
    },
    {
      id: 'assignedTo',
      label: 'Assigned To',
      type: 'searchable-select',
      options: usersData?.users?.map(mapDropdownUserToOption) || [],
      placeholder: 'All Users',
    },
    {
      id: 'unassigned',
      label: 'Show Unassigned',
      type: 'single-select',
      options: [
        { value: 'true', label: 'Yes' },
      ],
      placeholder: 'All',
    },
  ], [customersData, engagementsData, assignmentGroupsData, usersData, filterCustomer]);

  const customerOptions = useMemo(
    () =>
      customersData?.customers?.map((c: { id: string; accountShortName?: string; accountName?: string; name?: string }) => ({
        value: c.id,
        label: c.accountShortName || c.accountName || c.name || c.id,
      })) ?? [],
    [customersData]
  );

  const engagementOptions = useMemo(
    () =>
      engagementsData?.engagements?.map((e: { id: string; name: string }) => ({
        value: e.id,
        label: e.name,
      })) ?? [],
    [engagementsData]
  );

  const assignmentGroupOptions = useMemo(
    () =>
      assignmentGroupsData?.map((g: { id: string; name: string }) => ({
        value: g.id,
        label: g.name,
      })) ?? [],
    [assignmentGroupsData]
  );

  const userFilterOptions = useMemo(
    (): Array<{ value: string; label: string }> =>
      usersData?.users?.map(mapDropdownUserToOption) ?? [],
    [usersData]
  );

  const assignedToOptions = useMemo(
    () => [{ value: '__unassigned__', label: 'Unassigned' }, ...userFilterOptions],
    [userFilterOptions]
  );

  const handleAssignedToColumnFilter = useCallback(
    (value: string | string[]) => {
      const v = typeof value === 'string' ? value : '';
      if (v === '__unassigned__') {
        patchSearchParams({ assignedTo: null, unassigned: 'true', page: '1' });
      } else if (v) {
        patchSearchParams({ assignedTo: v, unassigned: null, page: '1' });
      } else {
        patchSearchParams({ assignedTo: null, unassigned: null, page: '1' });
      }
    },
    [patchSearchParams]
  );

  const assignedToColumnValue = filterUnassigned === 'true' ? '__unassigned__' : filterAssignedTo;

  const createdTimeRangeOptions = useMemo(
    () => [...timeRangeOptions, { value: 'all', label: 'All Time' }],
    []
  );

  const renderColumnHeader = useCallback(
    (columnKey: string, columnLabel: string) => {
      switch (columnKey) {
        case 'number':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterNumber}
              placeholder="Filter by number (comma or space separated)..."
              onChange={(v) => {
                patchSearchParams({ number: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'shortDescription':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterDescription}
              placeholder="Filter by description..."
              onChange={(v) => {
                patchSearchParams({ desc: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'contact':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="searchable-select"
              options={contactFilterOptions ?? []}
              value={filterContact}
              placeholder="All Contacts"
              onChange={(v) => {
                patchSearchParams({ contact: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'customer':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="searchable-select"
              options={customerOptions}
              value={filterCustomer}
              placeholder="All Customers"
              onChange={(v) => {
                patchSearchParams({
                  customer: typeof v === 'string' && v ? v : null,
                  engagement: null,
                  page: '1',
                });
              }}
            />
          );
        case 'engagement':
          if (!filterCustomer) return columnLabel;
          return (
            <TableColumnFilter
              label={columnLabel}
              type="multi-select"
              options={engagementOptions}
              value={filterEngagement}
              placeholder="All Engagements"
              onChange={(v) => {
                const arr = Array.isArray(v) ? v : [];
                patchSearchParams({ engagement: arr.length > 0 ? arr.join(',') : null, page: '1' });
              }}
            />
          );
        case 'priority':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="multi-select"
              options={priorityOptions}
              value={filterPriority}
              placeholder="All Priorities"
              onChange={(v) => {
                const arr = Array.isArray(v) ? v : [];
                patchSearchParams({ priority: arr.length > 0 ? arr.join(',') : null, page: '1' });
              }}
            />
          );
        case 'state':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="multi-select"
              options={stateOptions}
              value={filterState}
              placeholder="All States"
              onChange={(v) => {
                const arr = Array.isArray(v) ? v : [];
                patchSearchParams({ state: arr.length > 0 ? arr.join(',') : null, page: '1' });
              }}
            />
          );
        case 'assignedTo':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="searchable-select"
              options={assignedToOptions}
              value={assignedToColumnValue}
              placeholder="All Users"
              onChange={handleAssignedToColumnFilter}
            />
          );
        case 'assignmentGroup':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="multi-select"
              options={assignmentGroupOptions}
              value={filterAssignmentGroup}
              placeholder="All Groups"
              onChange={(v) => {
                const arr = Array.isArray(v) ? v : [];
                patchSearchParams({ groups: arr.length > 0 ? arr.join(',') : null, page: '1' });
              }}
            />
          );
        case 'category':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterCategory}
              placeholder="Filter by category..."
              onChange={(v) => {
                patchSearchParams({ category: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'channel':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={channelOptions}
              value={filterChannel}
              placeholder="All Channels"
              onChange={(v) => {
                patchSearchParams({ channel: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'impact':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="multi-select"
              options={impactOptions}
              value={filterImpact}
              placeholder="All Impact"
              onChange={(v) => {
                const arr = Array.isArray(v) ? v : [];
                patchSearchParams({ impact: arr.length > 0 ? arr.join(',') : null, page: '1' });
              }}
            />
          );
        case 'urgency':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="multi-select"
              options={urgencyOptions}
              value={filterUrgency}
              placeholder="All Urgency"
              onChange={(v) => {
                const arr = Array.isArray(v) ? v : [];
                patchSearchParams({ urgency: arr.length > 0 ? arr.join(',') : null, page: '1' });
              }}
            />
          );
        case 'createdBy':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="searchable-select"
              options={userFilterOptions}
              value={filterCreatedBy}
              placeholder="All Users"
              onChange={(v) => {
                patchSearchParams({ createdBy: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'createdAt':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="time-range"
              options={createdTimeRangeOptions}
              value={filterTimeRange || 'all'}
              placeholder="All Time"
              active={searchParams.has('time')}
              onChange={(v) => {
                const val = typeof v === 'string' ? v : '';
                const timeParam = serializeServiceRequestTimeRangeForUrl(val === 'all' ? '' : val);
                patchSearchParams({ time: timeParam, page: '1' });
              }}
            />
          );
        case 'slaBreached':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={[{ value: 'true', label: 'SLA Breached' }]}
              value={filterSlaBreached ? 'true' : ''}
              placeholder="All"
              onChange={(v) => {
                patchSearchParams({ sla: v === 'true' ? '1' : null, page: '1' });
              }}
            />
          );
        default:
          return columnLabel;
      }
    },
    [
      filterNumber,
      filterDescription,
      contactFilterOptions,
      filterContact,
      customerOptions,
      filterCustomer,
      engagementOptions,
      filterEngagement,
      filterPriority,
      filterState,
      assignedToOptions,
      assignedToColumnValue,
      handleAssignedToColumnFilter,
      assignmentGroupOptions,
      filterAssignmentGroup,
      filterCategory,
      filterChannel,
      filterImpact,
      filterUrgency,
      userFilterOptions,
      filterCreatedBy,
      createdTimeRangeOptions,
      filterTimeRange,
      searchParams,
      filterSlaBreached,
      patchSearchParams,
    ]
  );

  // Build active filter chips
  const activeFilterChips: FilterChip[] = useMemo(() => {
    const chips: FilterChip[] = [];

    if (appliedSearchTerm) {
      chips.push({
        id: 'search',
        label: 'Search',
        value: `"${appliedSearchTerm}"`,
        color: 'blue',
      });
    }

    if (filterNumber) {
      chips.push({ id: 'number', label: 'Number', value: filterNumber, color: 'blue' });
    }

    if (filterDescription) {
      chips.push({ id: 'desc', label: 'Description', value: filterDescription, color: 'blue' });
    }

    if (filterContact) {
      const option = contactFilterOptions?.find((o) => o.value === filterContact);
      chips.push({
        id: 'contact',
        label: 'Contact',
        value: option?.label || filterContact,
        color: 'teal',
      });
    }

    if (filterState.length > 0) {
      chips.push({
        id: 'state',
        label: 'State',
        value: filterState.map(s => stateLabels[s as keyof typeof stateLabels] || s).join(', '),
        color: 'purple',
      });
    }

    if (filterPriority.length > 0) {
      chips.push({
        id: 'priority',
        label: 'Priority',
        value: filterPriority.map(p => priorityLabels[p as keyof typeof priorityLabels] || p).join(', '),
        color: 'orange',
      });
    }

    if (filterTimeRange) {
      let timeLabel = '';
      if (filterTimeRange.startsWith('custom:')) {
        const [, dates] = filterTimeRange.split('custom:');
        const [from, to] = dates.split(',');
        timeLabel = `${from} to ${to}`;
      } else {
        const timeOption = timeRangeOptions.find(t => t.value === filterTimeRange);
        timeLabel = timeOption?.label || filterTimeRange;
      }
      chips.push({
        id: 'timeRange',
        label: 'Time',
        value: timeLabel,
        color: 'orange',
      });
    }

    if (filterCustomer) {
      const customer = customersData?.customers?.find((c: any) => c.id === filterCustomer);
      chips.push({
        id: 'customer',
        label: 'Customer',
        value: customer?.accountName || customer?.name || 'Unknown',
        color: 'teal',
      });
    }

    if (filterEngagement.length > 0) {
      const engagementNames = filterEngagement.map(engId => {
        const engagement = engagementsData?.engagements?.find((e: any) => e.id === engId);
        return engagement?.name || 'Unknown';
      });
      chips.push({
        id: 'engagement',
        label: 'Engagement',
        value: engagementNames.join(', '),
        color: 'cyan',
      });
    }

    if (appliedAlertNumber) {
      chips.push({
        id: 'alertNumber',
        label: 'Alert Number',
        value: appliedAlertNumber,
        color: 'red',
      });
    }

    if (filterAssignmentGroup.length > 0) {
      const groupNames = filterAssignmentGroup.map(groupId => {
        const group = assignmentGroupsData?.find((g: { id: string; name: string }) => g.id === groupId);
        return group?.name || 'Unknown';
      });
      chips.push({
        id: 'assignmentGroup',
        label: 'Assignment Group',
        value: groupNames.join(', '),
        color: 'purple',
      });
    }

    if (filterAssignedToMe) {
      chips.push({
        id: 'assignedToMe',
        label: 'Assigned To Me',
        value: filterAssignedToMe === 'true' ? 'Yes' : 'No',
        color: filterAssignedToMe === 'true' ? 'green' : 'gray',
      });
    }

    if (filterAssignedTo) {
      const assignedUser = userFilterOptions.find((o) => o.value === filterAssignedTo);
      chips.push({
        id: 'assignedTo',
        label: 'Assigned To',
        value: assignedUser?.label || 'Unknown',
        color: 'teal',
      });
    }

    if (filterCategory) {
      chips.push({ id: 'category', label: 'Category', value: filterCategory, color: 'purple' });
    }

    if (filterChannel) {
      chips.push({
        id: 'channel',
        label: 'Channel',
        value: channelOptions.find((o) => o.value === filterChannel)?.label || filterChannel,
        color: 'orange',
      });
    }

    if (filterImpact.length > 0) {
      chips.push({
        id: 'impact',
        label: 'Impact',
        value: filterImpact.map((v) => impactOptions.find((o) => o.value === v)?.label || v).join(', '),
        color: 'orange',
      });
    }

    if (filterUrgency.length > 0) {
      chips.push({
        id: 'urgency',
        label: 'Urgency',
        value: filterUrgency.map((v) => urgencyOptions.find((o) => o.value === v)?.label || v).join(', '),
        color: 'orange',
      });
    }

    if (filterCreatedBy) {
      const user = userFilterOptions.find((o) => o.value === filterCreatedBy);
      chips.push({
        id: 'createdBy',
        label: 'Created By',
        value: user?.label || filterCreatedBy,
        color: 'teal',
      });
    }

    if (filterSlaBreached) {
      chips.push({ id: 'sla', label: 'SLA Breached', value: 'Yes', color: 'red' });
    }

    if (filterUnassigned === 'true') {
      chips.push({
        id: 'unassigned',
        label: 'Show Unassigned',
        value: 'Yes',
        color: 'orange',
      });
    }

    return chips;
  }, [appliedSearchTerm, filterNumber, filterDescription, filterContact, filterState, filterPriority, filterTimeRange, filterCustomer, filterEngagement, filterCategory, filterChannel, appliedAlertNumber, filterAssignmentGroup, filterAssignedToMe, filterAssignedTo, filterUnassigned, filterImpact, filterUrgency, filterCreatedBy, filterSlaBreached, customersData, engagementsData, assignmentGroupsData, contactFilterOptions, userFilterOptions]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['service-requests', page, pageSize, effectiveSearch, filterState, filterCategory, filterChannel, filterPriority, filterTimeRange, filterCustomer, filterEngagement, appliedAlertNumber, filterAssignmentGroup, filterAssignedToMe, filterAssignedTo, filterUnassigned, filterImpact, filterUrgency, filterContact, filterCreatedBy, filterSlaBreached, timezoneOffset],
    queryFn: async () => {
      // Build time range params
      let timeRangeParam = '';
      let dateFrom = '';
      let dateTo = '';
      if (filterTimeRange) {
        if (filterTimeRange.startsWith('custom:')) {
          const [, dates] = filterTimeRange.split('custom:');
          const [from, to] = dates.split(',');
          dateFrom = from;
          dateTo = to;
        } else if (filterTimeRange.startsWith('preset:')) {
          const presetName = filterTimeRange.replace('preset:', '') as DurationPreset;
          const range = getDurationPresetRange(presetName);
          dateFrom = range.from;
          dateTo = range.to;
        } else {
          timeRangeParam = filterTimeRange;
        }
      }

      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('limit', pageSize.toString());
      params.set('ticketType', 'SERVICE_REQUEST');
      if (effectiveSearch) params.set('search', effectiveSearch);
      if (filterState.length > 0) params.set('state', filterState.join(','));
      if (filterCategory) params.set('category', filterCategory);
      if (filterChannel) params.set('channel', filterChannel);
      if (filterPriority.length > 0) params.set('priority', filterPriority.join(','));
      if (timeRangeParam) params.set('timeRange', timeRangeParam);
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      if (dateFrom || dateTo) params.set('timezoneOffset', String(timezoneOffset));
      if (filterCustomer) params.set('customerId', filterCustomer);
      if (filterEngagement.length > 0) params.set('engagementId', filterEngagement.join(','));
      if (appliedAlertNumber) params.set('alertNumber', appliedAlertNumber);
      if (filterAssignmentGroup.length > 0) {
        params.set('assignmentGroupId', filterAssignmentGroup.join(','));
      }
      if (filterAssignedToMe) params.set('assignedToMe', filterAssignedToMe);
      if (filterAssignedTo) params.set('assignedToId', filterAssignedTo);
      if (filterUnassigned) params.set('unassigned', filterUnassigned);
      if (filterImpact.length > 0) params.set('impact', filterImpact.join(','));
      if (filterUrgency.length > 0) params.set('urgency', filterUrgency.join(','));
      if (filterContact) params.set('callerId', filterContact);
      if (filterCreatedBy) params.set('createdById', filterCreatedBy);
      if (filterSlaBreached) params.set('slaBreached', 'true');

      const response = await api.get(`/cases?${params}`);
      return response.data;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    staleTime: 30000 // 30 seconds
  });

  // Log "Service Requests List Viewed" when data is loaded successfully
  useEffect(() => {
    if (data && user && !hasLoggedView.current && !isLoading) {
      hasLoggedView.current = true;
      trackServiceRequestOperation(
        'Service Requests List',
        user.email || user.firstName || 'Unknown',
        {
          action: 'Service Requests List Viewed',
        },
        {
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role,
          roleType: user.roleType,
          roleLevel: user.roleLevel,
          userClass: user.class,
          customerId: user.customerId,
          customerName: user.customerName,
          engagementId: user.engagementId,
          engagementName: user.engagementName,
          isServiceDeskUser: isServiceDeskUser,
        }
      );
    }
  }, [data, user, isLoading, isServiceDeskUser]);

  // Fetch service request stats for the summary bar (with same filters as list)
  const { data: statsData } = useQuery({
    queryKey: ['service-request-stats', effectiveSearch, filterState, filterCategory, filterChannel, filterPriority, filterTimeRange, filterCustomer, filterEngagement, appliedAlertNumber, filterAssignmentGroup, filterAssignedToMe, filterAssignedTo, filterUnassigned, filterImpact, filterUrgency, filterContact, filterCreatedBy, filterSlaBreached, timezoneOffset],
    queryFn: async () => {
      // Build time range params (same as list query)
      let timeRangeParam = '';
      let dateFrom = '';
      let dateTo = '';
      if (filterTimeRange) {
        if (filterTimeRange.startsWith('custom:')) {
          const [, dates] = filterTimeRange.split('custom:');
          const [from, to] = dates.split(',');
          dateFrom = from;
          dateTo = to;
        } else if (filterTimeRange.startsWith('preset:')) {
          const presetName = filterTimeRange.replace('preset:', '') as DurationPreset;
          const range = getDurationPresetRange(presetName);
          dateFrom = range.from;
          dateTo = range.to;
        } else {
          timeRangeParam = filterTimeRange;
        }
      }

      const params = new URLSearchParams();
      params.set('ticketType', 'SERVICE_REQUEST');
      if (effectiveSearch) params.set('search', effectiveSearch);
      if (filterState.length > 0) params.set('state', filterState.join(','));
      if (filterCategory) params.set('category', filterCategory);
      if (filterChannel) params.set('channel', filterChannel);
      if (filterPriority.length > 0) params.set('priority', filterPriority.join(','));
      if (timeRangeParam) params.set('timeRange', timeRangeParam);
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      if (dateFrom || dateTo) params.set('timezoneOffset', String(timezoneOffset));
      if (filterCustomer) params.set('customerId', filterCustomer);
      if (filterEngagement.length > 0) params.set('engagementId', filterEngagement.join(','));
      if (appliedAlertNumber) params.set('alertNumber', appliedAlertNumber);
      if (filterAssignmentGroup.length > 0) {
        params.set('assignmentGroupId', filterAssignmentGroup.join(','));
      }
      if (filterAssignedToMe) params.set('assignedToMe', filterAssignedToMe);
      if (filterAssignedTo) params.set('assignedToId', filterAssignedTo);
      if (filterUnassigned) params.set('unassigned', filterUnassigned);
      if (filterImpact.length > 0) params.set('impact', filterImpact.join(','));
      if (filterUrgency.length > 0) params.set('urgency', filterUrgency.join(','));
      if (filterContact) params.set('callerId', filterContact);
      if (filterCreatedBy) params.set('createdById', filterCreatedBy);
      if (filterSlaBreached) params.set('slaBreached', 'true');

      const response = await api.get(`/cases/stats?${params}`);
      return response.data;
    },
    refetchOnWindowFocus: false,
    staleTime: 30000 // 30 seconds - match list query
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    patchSearchParams({
      search: searchInput.trim() || null,
      page: '1',
    });
  };

  // Handle filter dropdown changes
  const handleFilterChange = (sectionId: string, value: string | string[]) => {
    switch (sectionId) {
      case 'state': {
        const arr = value as string[];
        patchSearchParams({
          state: arr.length > 0 ? arr.join(',') : null,
          page: '1',
        });
        break;
      }
      case 'priority': {
        const arr = value as string[];
        patchSearchParams({
          priority: arr.length > 0 ? arr.join(',') : null,
          page: '1',
        });
        break;
      }
      case 'timeRange': {
        const v = value as string;
        const timeParam = serializeServiceRequestTimeRangeForUrl(v);
        patchSearchParams({
          time: timeParam,
          page: '1',
        });
        break;
      }
      case 'customer':
        patchSearchParams({
          customer: (value as string) || null,
          engagement: null,
          page: '1',
        });
        break;
      case 'engagement': {
        const arr = value as string[];
        patchSearchParams({
          engagement: arr.length > 0 ? arr.join(',') : null,
          page: '1',
        });
        break;
      }
      case 'alertNumber':
        setFilterAlertNumber(value as string);
        break;
      case 'assignmentGroup': {
        const arr = value as string[];
        patchSearchParams({
          groups: arr.length > 0 ? arr.join(',') : null,
          page: '1',
        });
        break;
      }
      case 'assignedToMe':
        patchSearchParams({
          assignedToMe:
            value === 'true' || value === 'false' ? (value as string) : null,
          page: '1',
        });
        break;
      case 'assignedTo':
        patchSearchParams({
          assignedTo: (value as string) || null,
          page: '1',
        });
        break;
      case 'unassigned':
        patchSearchParams({
          unassigned: value === 'true' ? 'true' : null,
          page: '1',
        });
        break;
    }
  };

  // Handle applying alert number filter
  const handleApplyFilters = () => {
    if (filterAlertNumber !== appliedAlertNumber) {
      patchSearchParams({
        alert: filterAlertNumber.trim() || null,
        page: '1',
      });
    }
  };

  // Handle removing a single filter chip
  const handleRemoveFilterChip = (chipId: string) => {
    switch (chipId) {
      case 'search':
        setSearchInput('');
        patchSearchParams({ search: null, page: '1' });
        break;
      case 'number':
        patchSearchParams({ number: null, page: '1' });
        break;
      case 'desc':
        patchSearchParams({ desc: null, page: '1' });
        break;
      case 'contact':
        patchSearchParams({ contact: null, page: '1' });
        break;
      case 'state':
        patchSearchParams({ state: null, page: '1' });
        break;
      case 'priority':
        patchSearchParams({ priority: null, page: '1' });
        break;
      case 'timeRange':
        patchSearchParams({ time: 'all', page: '1' });
        break;
      case 'customer':
        patchSearchParams({ customer: null, engagement: null, page: '1' });
        break;
      case 'engagement':
        patchSearchParams({ engagement: null, page: '1' });
        break;
      case 'alertNumber':
        setFilterAlertNumber('');
        patchSearchParams({ alert: null, page: '1' });
        break;
      case 'assignmentGroup':
        patchSearchParams({ groups: null, page: '1' });
        break;
      case 'assignedToMe':
        patchSearchParams({ assignedToMe: null, page: '1' });
        break;
      case 'assignedTo':
        patchSearchParams({ assignedTo: null, page: '1' });
        break;
      case 'unassigned':
        patchSearchParams({ unassigned: null, page: '1' });
        break;
      case 'category':
        patchSearchParams({ category: null, page: '1' });
        break;
      case 'channel':
        patchSearchParams({ channel: null, page: '1' });
        break;
      case 'impact':
        patchSearchParams({ impact: null, page: '1' });
        break;
      case 'urgency':
        patchSearchParams({ urgency: null, page: '1' });
        break;
      case 'createdBy':
        patchSearchParams({ createdBy: null, page: '1' });
        break;
      case 'sla':
        patchSearchParams({ sla: null, page: '1' });
        break;
    }
  };

  // Handle clearing all filters
  const handleClearAllFilters = () => {
    setSearchInput('');
    setFilterAlertNumber('');
    patchSearchParams({
      search: null,
      number: null,
      desc: null,
      contact: null,
      state: null,
      priority: null,
      time: 'all',
      customer: null,
      engagement: null,
      alert: null,
      groups: null,
      assignedToMe: null,
      assignedTo: null,
      unassigned: null,
      category: null,
      channel: null,
      impact: null,
      urgency: null,
      createdBy: null,
      sla: null,
      page: '1',
    });
  };


  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">Error loading service requests. Please try again.</p>
        </div>
      </div>
    );
  }


  return (
    <div className="p-3 flex flex-col h-full min-h-0 overflow-hidden">
      {/* Compact Header Bar */}
      <div className="bg-white shadow rounded-lg mb-2 flex-shrink-0">
        <div className="px-4 py-2">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
            {/* Left: Title and Filter */}
            <div className="flex items-center gap-4">
              <h1 className="text-lg font-semibold text-gray-900 whitespace-nowrap">Service Requests</h1>
              <FilterDropdown
                sections={filterSections}
                values={{
                  state: filterState,
                  priority: filterPriority,
                  timeRange: filterTimeRange,
                  customer: filterCustomer,
                  engagement: filterEngagement,
                  alertNumber: filterAlertNumber,
                  assignmentGroup: filterAssignmentGroup,
                  assignedToMe: filterAssignedToMe,
                  assignedTo: filterAssignedTo,
                  unassigned: filterUnassigned,
                }}
                onChange={handleFilterChange}
                onApply={handleApplyFilters}
              />
            </div>

            {/* Right: Search, New Button, and Settings */}
            <form onSubmit={handleSearch} className="flex items-center gap-2">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search service requests or numbers (comma/space separated)..."
                  className="pl-8 pr-3 py-1.5 w-64 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>
              <button
                type="submit"
                disabled={!isTicketSearchInputValid(searchInput)}
                className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Search
              </button>
              <Link
                to="/service-requests/new"
                className="inline-flex items-center px-3 py-1.5 text-sm border border-transparent rounded-md shadow-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
              >
                <PlusIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
                New
              </Link>
              <ListLayoutSettingsButton onClick={() => setShowLayoutSettings(true)} />
              <RefreshButton
                queryKeys={['service-requests']}
                tooltip="Refresh service requests"
                size="md"
                isLoading={isLoading}
              />
            </form>
          </div>

          {/* Active Filters - Inline */}
          {activeFilterChips.length > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-100">
              <ActiveFilterChips
                chips={activeFilterChips}
                onRemove={handleRemoveFilterChip}
                onClearAll={handleClearAllFilters}
                onSaveAsDefault={handleSaveFilters}
                isSaving={isFilterSaving}
                hasUnsavedChanges={hasUnsavedFilterChanges}
              />
            </div>
          )}

          {/* Stats Summary Bar */}
          <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between text-sm">
            <div className="flex items-center gap-6">
              <span className="text-gray-600">
                Total: <span className="font-semibold text-gray-900">{statsData?.total?.toLocaleString() ?? data?.total?.toLocaleString() ?? '-'}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-red-500"></span>
                <span className="text-gray-600">Critical:</span>
                <span className="font-semibold text-gray-900">{statsData?.byPriority?.critical_1 ?? '-'}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                <span className="text-gray-600">High:</span>
                <span className="font-semibold text-gray-900">{statsData?.byPriority?.high_2 ?? '-'}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                <span className="text-gray-600">In Progress:</span>
                <span className="font-semibold text-gray-900">{statsData?.byState?.in_progress ?? '-'}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-gray-400"></span>
                <span className="text-gray-600">Unassigned:</span>
                <span className="font-semibold text-gray-900">{statsData?.unassigned ?? '-'}</span>
              </span>
            </div>
            <div className="flex items-center gap-3">
              {/* Export CSV Button */}
              {data?.cases && data.cases.length > 0 && (
                <GenericExportButton
                  config={ExportConfigs.SERVICE_REQUESTS}
                  filters={exportFilters}
                  columns={exportColumns}
                  totalRows={data?.total}
                  disabled={!data?.cases || data.cases.length === 0}
                  onExportStart={() => {
                    // Log export button click
                    if (user) {
                      trackServiceRequestOperation(
                        'Service Requests List',
                        user.email || user.firstName || 'Unknown',
                        {
                          action: 'Export Button Clicked',
                        },
                        {
                          firstName: user.firstName,
                          lastName: user.lastName,
                          role: user.role,
                          roleType: user.roleType,
                          roleLevel: user.roleLevel,
                          userClass: user.class,
                          customerId: user.customerId,
                          customerName: user.customerName,
                          engagementId: user.engagementId,
                          engagementName: user.engagementName,
                          isServiceDeskUser: isServiceDeskUser,
                        }
                      );
                    }
                  }}
                />
              )}
              {(statsData?.slaBreached ?? 0) > 0 && (
                <span className="flex items-center gap-1.5 text-red-600">
                  <ExclamationTriangleIcon className="h-4 w-4" />
                  <span>SLA Breached:</span>
                  <span className="font-semibold">{statsData?.slaBreached}</span>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Service Requests Table - Takes remaining space */}
      <div className="bg-white shadow rounded-lg overflow-hidden flex-1 min-h-0 flex flex-col">
        <div className="overflow-scroll flex-1" style={{ scrollbarGutter: 'stable' }}>
          <table className="min-w-full divide-y divide-gray-200" style={{ minWidth: '1200px' }}>
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                {visibleColumns.map((col) => (
                  <th key={col.key} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    {renderColumnHeader(col.key, col.label)}
                  </th>
                ))}
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data?.cases?.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumns.length + 1} className="px-3 py-2 text-sm text-gray-500 text-center">
                    No service requests found
                  </td>
                </tr>
              ) : (
                data?.cases?.map((request: ServiceRequest) => {
                  // Column renderer function
                  const renderColumn = (columnKey: string) => {
                    switch (columnKey) {
                      case 'number':
                        return (
                          <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm font-medium">
                            <Link
                              to={`/service-requests/${request.id || request._id}`}
                              state={{ serviceRequestsListSearch: location.search }}
                              className="text-indigo-600 hover:text-indigo-900 hover:underline"
                              title="View service request details"
                            >
                              {request.number}
                            </Link>
                          </td>
                        );
                      case 'shortDescription':
                        return (
                          <td key={columnKey} className="px-3 py-2 text-sm text-gray-900">
                            <div className="max-w-xs truncate">{stripHtml(request.shortDescription)}</div>
                          </td>
                        );
                      case 'customer':
                        return (
                          <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                            {request.Customer?.accountName || '-'}
                          </td>
                        );
                      case 'engagement':
                        return (
                          <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                            {request.Engagement?.name || '-'}
                          </td>
                        );
                      case 'state':
                        return (
                          <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm">
                            <div className="flex items-center">
                              {stateIcons[request.state as keyof typeof stateIcons] || <DocumentTextIcon className="h-5 w-5 text-gray-500" />}
                              <span className="ml-2 text-gray-900">
                                {stateLabels[request.state as keyof typeof stateLabels] || request.state}
                              </span>
                            </div>
                          </td>
                        );
                      case 'priority':
                        return (
                          <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                              priorityColors[request.priority as keyof typeof priorityColors] || 'bg-gray-100 text-gray-800 border-gray-200'
                            }`}>
                              {priorityLabels[request.priority as keyof typeof priorityLabels] || request.priority}
                            </span>
                          </td>
                        );
                      case 'assignedTo':
                        return (
                          <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                            {request.assignedTo ? (
                              `${request.assignedTo.firstName || ''} ${request.assignedTo.lastName || ''}`.trim() ||
                              request.assignedTo.email ||
                              <span className="text-gray-500">Unassigned</span>
                            ) : (
                              <span className="text-gray-500">Unassigned</span>
                            )}
                          </td>
                        );
                      case 'assignmentGroup':
                        return (
                          <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                            {request.AssignmentGroup?.name || '-'}
                          </td>
                        );
                      case 'contact':
                        return (
                          <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                            {request.contact ? (
                              `${request.contact.firstName || ''} ${request.contact.lastName || ''}`.trim() ||
                              request.contact.email ||
                              '-'
                            ) : '-'}
                          </td>
                        );
                      case 'category':
                        return (
                          <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                            {request.category || '-'}
                          </td>
                        );
                      case 'channel':
                        return (
                          <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                            {request.channel || '-'}
                          </td>
                        );
                      case 'createdAt':
                        return (
                          <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                            {request.createdAt ? (
                              <div className="flex flex-col">
                                <span>{new Date(request.createdAt).toLocaleDateString()}</span>
                                <span className="text-xs">{new Date(request.createdAt).toLocaleTimeString()}</span>
                              </div>
                            ) : '-'}
                          </td>
                        );
                      case 'createdBy':
                        return (
                          <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                            {request.createdBy ?
                              `${request.createdBy.firstName} ${request.createdBy.lastName}` :
                              '-'
                            }
                          </td>
                        );
                      case 'impact':
                        return (
                          <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                            {request.impact ? request.impact.replace('_', ' ') : '-'}
                          </td>
                        );
                      case 'urgency':
                        return (
                          <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                            {request.urgency ? request.urgency.replace('_', ' ') : '-'}
                          </td>
                        );
                      case 'slaPlan':
                        return (
                          <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                            {request.ServicePlan?.name || '-'}
                          </td>
                        );
                      case 'slaBreached':
                        return (
                          <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm">
                            <SlaStatusDisplay slaStatus={request.slaStatus} />
                          </td>
                        );
                      case 'responseBreachReason':
                        return (
                          <td key={columnKey} className="px-3 py-2 text-sm text-gray-900">
                            <TruncatedTextWithTooltip text={(request as any).responseBreachReason} />
                          </td>
                        );
                      case 'ongoingBreachReason':
                        return (
                          <td key={columnKey} className="px-3 py-2 text-sm text-gray-900">
                            <TruncatedTextWithTooltip text={(request as any).ongoingBreachReason} />
                          </td>
                        );
                      case 'resolutionBreachReason':
                        return (
                          <td key={columnKey} className="px-3 py-2 text-sm text-gray-900">
                            <TruncatedTextWithTooltip text={(request as any).resolutionBreachReason} />
                          </td>
                        );
                      case 'resolvedAt':
                        return (
                          <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                            {(request as any).resolvedAt ? (
                              <div className="flex flex-col">
                                <span>{new Date((request as any).resolvedAt).toLocaleDateString()}</span>
                                <span className="text-xs">{new Date((request as any).resolvedAt).toLocaleTimeString()}</span>
                              </div>
                            ) : '-'}
                          </td>
                        );
                      case 'resolutionNotes':
                        return (
                          <td key={columnKey} className="px-3 py-2 text-sm text-gray-900">
                            <div className="max-w-xs truncate">{(request as any).resolutionNotes ? stripHtml((request as any).resolutionNotes) : '-'}</div>
                          </td>
                        );
                      case 'closedAt':
                        return (
                          <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                            {(request as any).closedAt ? (
                              <div className="flex flex-col">
                                <span>{new Date((request as any).closedAt).toLocaleDateString()}</span>
                                <span className="text-xs">{new Date((request as any).closedAt).toLocaleTimeString()}</span>
                              </div>
                            ) : '-'}
                          </td>
                        );
                      case 'closureNotes':
                        return (
                          <td key={columnKey} className="px-3 py-2 text-sm text-gray-900">
                            <div className="max-w-xs truncate">{(request as any).closureNotes ? stripHtml((request as any).closureNotes) : '-'}</div>
                          </td>
                        );
                      default:
                        return (
                          <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                            -
                          </td>
                        );
                    }
                  };

                  return (
                  <tr key={request.id || request._id} className="hover:bg-gray-50">
                    {visibleColumns.map((col) => renderColumn(col.key))}
                    <td className="px-3 py-2 whitespace-nowrap text-sm">
                      <button
                        onClick={() => openEmailModal(request)}
                        className="text-blue-600 hover:text-blue-900 inline-flex items-center"
                        title="Email ticket details"
                      >
                        <EnvelopeIcon className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Controls - Always visible */}
      <div className="mt-2 flex flex-col sm:flex-row items-center justify-between gap-2 bg-white px-3 py-2 rounded-lg shadow flex-shrink-0" style={{ minHeight: '48px' }}>
        {data?.total > 0 ? (
          <>
          {/* Left: Records per page dropdown */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">Show</span>
            <select
              value={pageSize}
              onChange={(e) => {
                patchSearchParams({
                  pageSize: e.target.value,
                  page: '1',
                });
              }}
              className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              {PAGE_SIZE_OPTIONS.map((size, index) => {
                const previousSize = index > 0 ? PAGE_SIZE_OPTIONS[index - 1] : 0;
                const isDisabled = previousSize >= data.total && size > data.total;
                return (
                  <option key={size} value={size} disabled={isDisabled}>
                    {size}
                  </option>
                );
              })}
            </select>
            <span className="text-sm text-gray-700">entries</span>
          </div>

          {/* Center: Entry count */}
          <div className="text-sm text-gray-700">
            Showing {Math.min((page - 1) * pageSize + 1, data.total)} to {Math.min(page * pageSize, data.total)} of {data.total} entries
          </div>

          {/* Right: Pagination buttons */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => patchSearchParams({ page: '1' })}
              disabled={page === 1}
              className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="First page"
            >
              &laquo;
            </button>
            <button
              onClick={() => patchSearchParams({ page: String(page - 1) })}
              disabled={page === 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>

            {/* Page numbers */}
            {(() => {
              const totalPages = Math.ceil(data.total / pageSize);
              const pages: (number | string)[] = [];

              if (totalPages <= 7) {
                for (let i = 1; i <= totalPages; i++) pages.push(i);
              } else {
                if (page <= 3) {
                  pages.push(1, 2, 3, 4, '...', totalPages);
                } else if (page >= totalPages - 2) {
                  pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
                } else {
                  pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
                }
              }

              return pages.map((p, idx) => (
                p === '...' ? (
                  <span key={`ellipsis-${idx}`} className="px-2 py-1 text-sm text-gray-500">...</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => patchSearchParams({ page: String(p) })}
                    className={`px-3 py-1 text-sm border rounded-md ${
                      page === p
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {p}
                  </button>
                )
              ));
            })()}

            <button
              onClick={() => patchSearchParams({ page: String(page + 1) })}
              disabled={page >= Math.ceil(data.total / pageSize)}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
            <button
              onClick={() =>
                patchSearchParams({
                  page: String(Math.ceil(data.total / pageSize)),
                })
              }
              disabled={page >= Math.ceil(data.total / pageSize)}
              className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Last page"
            >
              &raquo;
            </button>
          </div>
          </>
        ) : (
          <div className="text-sm text-gray-500 w-full text-center">No records</div>
        )}
      </div>

      {/* Email Ticket Modal */}
      <EmailTicketModal
        isOpen={emailModalState.isOpen}
        onClose={() => setEmailModalState({ isOpen: false, ticketId: '', ticketNumber: '', shortDescription: '' })}
        ticketId={emailModalState.ticketId}
        ticketType="SERVICE_REQUEST"
        ticketNumber={emailModalState.ticketNumber}
        shortDescription={emailModalState.shortDescription}
      />

      {/* List Layout Settings Modal */}
      <ListLayoutSettingsModal
        isOpen={showLayoutSettings}
        onClose={() => setShowLayoutSettings(false)}
        entityType="SERVICE_REQUEST"
        columns={layoutColumns}
        onSave={saveColumns}
        onReset={resetToDefault}
        isSaving={isLayoutSaving}
        isResetting={isLayoutResetting}
      />

      {/* Toast Notification */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 animate-slide-in-right">
          <div
            className={`rounded-lg shadow-lg px-3 py-2 max-w-md ${
              toast.type === 'success'
                ? 'bg-green-50 border border-green-200'
                : toast.type === 'error'
                ? 'bg-red-50 border border-red-200'
                : 'bg-yellow-50 border border-yellow-200'
            }`}
          >
            <div className="flex items-start gap-3">
              {toast.type === 'success' && (
                <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0" />
              )}
              {toast.type === 'error' && (
                <XMarkIcon className="h-6 w-6 text-red-600 flex-shrink-0" />
              )}
              {toast.type === 'warning' && (
                <QuestionMarkCircleIcon className="h-6 w-6 text-yellow-600 flex-shrink-0" />
              )}
              <div className="flex-1">
                <p
                  className={`text-sm font-medium ${
                    toast.type === 'success'
                      ? 'text-green-800'
                      : toast.type === 'error'
                      ? 'text-red-800'
                      : 'text-yellow-800'
                  }`}
                >
                  {toast.message}
                </p>
              </div>
              <button
                onClick={() => setToast(null)}
                className={`flex-shrink-0 ${
                  toast.type === 'success'
                    ? 'text-green-400 hover:text-green-600'
                    : toast.type === 'error'
                    ? 'text-red-400 hover:text-red-600'
                    : 'text-yellow-400 hover:text-yellow-600'
                }`}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceRequestList;